import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { Box, Button, Container, TextField, Typography } from "src/UILibrary"

import { ShowHeader } from "../../../states/showHeader"
import { useRecoilState } from "recoil"
import { useLocation, useNavigate } from "react-router-dom"
import { validateField } from "src/modules/validation"
import * as yup from "yup"
import { ShowToast } from "src/states/showToast"
import { AuthenticationService } from "src/openapi"
import { MEMBER_TYPE, RESPONSE_CODE, SNACKBAR_TYPES, USER_ATTRIBITE_KEY } from "src/modules/const"
import { getUserAttribute } from "src/modules/helper"
import { useLoginContext } from "src/hooks/useLoginContext"
import { ShowProgress } from "src/states/showProgress"

export const EmailVerification = () => {
  const { t } = useTranslation("translation", { keyPrefix: "signup" })

  const navigate = useNavigate()

  const { handleLogin } = useLoginContext()

  const [, setShowHeader] = useRecoilState(ShowHeader)
  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [code, setCode] = useState<string>("")

  const { state } = useLocation()
  const { requireSignIn } = state || ""
  const { email } = state || ""
  const { userId } = state || ""
  const { type } = state || 0
  const { password } = state || ""

  if (requireSignIn) {
    if (!email || !password) {
      navigate("/signup/type")
    }
  } else {
    if (!email || !userId || !type) {
      navigate("/signup/type")
    }
  }

  useEffect(() => {
    setShowHeader(false)
  })

  const validate = async () => {
    try {
      await validateField(
        yup.object().shape({
          verification_code: yup
            .string()
            .length(6, t("verify_code_length"))
            .matches(/^\d+$/, t("verify_code_number")),
        }),
        "verification_code",
        code
      )
    } catch (err: any) {
      setShowToast({ show: true, text: err.message, type: SNACKBAR_TYPES.ERROR })
      return false
    }
    return true
  }

  const getUserType = (userDatas: any[]) => {
    return parseInt(getUserAttribute(userDatas, USER_ATTRIBITE_KEY.USER_TYPE))
  }

  const getUserSub = (userDatas: any[]) => {
    return getUserAttribute(userDatas, USER_ATTRIBITE_KEY.AWS_ID)
  }

  const handleVerify = async () => {
    if (!(await validate())) {
      return
    }

    setShowProgress(true)
    AuthenticationService.verify({
      email: email,
      verificationCode: code,
    })
      .then((res) => {
        if (res.status === RESPONSE_CODE.SUCCESS) {
          if (requireSignIn) {
            AuthenticationService.signIn({
              email: email,
              password: password,
            })
              .then((res) => {
                setShowProgress(false)
                if (res.status === RESPONSE_CODE.SUCCESS) {
                  setShowToast({
                    show: true,
                    text: t("success_login"),
                    type: SNACKBAR_TYPES.SUCCESS,
                  })

                  const attributes = res.data?.UserData?.UserAttributes as any[]
                  const userType = getUserType(attributes)
                  const userID = res.data?.UserData?.Username

                  handleLogin(
                    res.data?.TokenData?.AuthenticationResult?.AccessToken,
                    userType,
                    userID
                  )

                  navigate("/")
                } else if (res.status === RESPONSE_CODE.NOT_APPROVED) {
                  setShowToast({
                    show: true,
                    text: t("please_wait_until_approve"),
                    type: SNACKBAR_TYPES.INFORMATION,
                  })
                  navigate("/signin")
                } else if (res.status === RESPONSE_CODE.NOT_FOUND) {
                  const attributes = res.data?.UserData?.UserAttributes as any[]
                  const userType = getUserType(attributes)
                  const userID = getUserSub(attributes)

                  switch (userType) {
                    case MEMBER_TYPE.USER:
                      navigate("/signup/user/detail", {
                        state: {
                          userId: userID,
                          email: email,
                        },
                      })
                      return
                    case MEMBER_TYPE.ADVISER:
                      navigate("/signup/adviser/detail", {
                        state: {
                          userId: userID,
                          email: email,
                        },
                      })
                      return
                    case MEMBER_TYPE.COMPANY:
                      navigate("/signup/company/detail", {
                        state: {
                          userId: userID,
                          email: email,
                        },
                      })
                      return
                    case MEMBER_TYPE.ADMINISTRATOR:
                      navigate("/signup/admin/detail", {
                        state: {
                          userId: userID,
                          email: email,
                        },
                      })
                      return
                  }
                } else {
                  setShowToast({ show: true, text: t("failed_verify"), type: SNACKBAR_TYPES.ERROR })
                  navigate("/signin")
                }
              })
              .catch(() => {
                setShowProgress(false)
                setShowToast({ show: true, text: t("failed_verify"), type: SNACKBAR_TYPES.ERROR })
                navigate("/signin")
              })
          } else {
            setShowProgress(false)
            switch (type) {
              case MEMBER_TYPE.USER:
                navigate("/signup/user/detail", {
                  state: { userId: userId, email: email },
                })
                return
              case MEMBER_TYPE.ADVISER:
                navigate("/signup/adviser/detail", {
                  state: { userId: userId, email: email },
                })
                return
              case MEMBER_TYPE.COMPANY:
                navigate("/signup/company/detail", {
                  state: { userId: userId, email: email },
                })
                return
              case MEMBER_TYPE.ADMINISTRATOR:
                navigate("/signup/admin/detail", {
                  state: { userId: userId, email: email },
                })
                return
            }
          }
        } else {
          setShowProgress(false)
          setShowToast({ show: true, text: res.message as string, type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("verification_failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleResend = async () => {
    setShowProgress(true)
    AuthenticationService.resend({
      email: email,
    })
      .then((res) => {
        setShowProgress(false)
        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("resend_success"), type: SNACKBAR_TYPES.SUCCESS })
        } else {
          setShowToast({ show: true, text: t("resend_failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("resend_failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ display: "flex", flexDirection: "column" }}>
        <Typography.Larger sx={{ mx: "auto", mt: "100px", color: "text.primary" }}>
          {t("verify_email")}
        </Typography.Larger>
        <Typography.Title sx={{ mx: "auto", mt: "50px", color: "text.primary", fontWeight: "400" }}>
          {t("verify_email_description")}
        </Typography.Title>
        <Box sx={{ display: "flex", mt: "30px", mx: "auto" }}>
          <TextField
            variant="standard"
            inputProps={{ sx: { fontSize: "20px", py: "10px" }, maxLength: 6 }}
            sx={{ width: "120px" }}
            placeholder="000000"
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
          <Button
            variant="outlined"
            sx={{ ml: "40px", height: "40px", my: "auto" }}
            onClick={() => handleResend()}
          >
            <Typography.ButtonText sx={{ color: "text.primary", width: "70px", fontSize: "18px" }}>
              {t("resend")}
            </Typography.ButtonText>
          </Button>
        </Box>
        <Button
          variant="contained"
          sx={{
            minWidth: "200px",
            py: "10px",
            fontSize: "1rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            my: "40px",
            mx: "auto",
            ":hover": {
              bgcolor: "#2DA389",
            },
          }}
          onClick={() => handleVerify()}
        >
          {t("verify")}
        </Button>
      </Container>
    </>
  )
}
